// @ts-nocheck
import cx from "classix";

import { Container } from "components/LandingPage/Container";
import Link from "next/link";
import { useEffect, useState } from "react";

import analytics from "@/utils/analytics/analytics";
import { SIGNUP_REDIRECT_TO_BILLING_MODAL } from "@/utils/authRedirect";

const plans = [
  {
    stripeId: "price_1OZBumJFWuBJJ6gcd7lFG8Ha",
    credits: "25 000",
    price: 9,
    id: "0000004JFGS2PKM95R06GG759D",
  },
  {
    stripeId: "price_1OZBuXJFWuBJJ6gcpgZHP3pC",
    credits: "50 000",
    price: 15,
    id: "0000004JFG1MEZRM2N538ZH3Q0",
  },
  {
    stripeId: "price_1OZBx5JFWuBJJ6gc5qEsEfBD",
    credits: "100 000",
    price: 25,
    id: "39d4aeda-6d47-4a01-8c52-16e380d3f905",
    popular: true,
  },
  {
    stripeId: "price_1OZBvOJFWuBJJ6gc5kCK5cFB",
    credits: "250 000",
    price: 49,
    id: "0000004JFGGVPCTETPYHPMYFVH",
  },
  {
    stripeId: "price_1OZBvhJFWuBJJ6gcOJfxsMWg",
    credits: "500 000",
    price: 89,
    id: "0000004JFG9CBVG421FPHJFWTF",
  },
];

export function Pricing({
  inApp = false,
  onBuy,
  initialPlanStripeId,
}: {
  inApp?: boolean;
  onBuy?: (planId: string) => void;
  initialPlanStripeId?: string | null;
} = {}) {
  const [selectedPlan, setSelectedPlan] = useState(plans[2]);

  useEffect(() => {
    if (!initialPlanStripeId) {
      return;
    }
    const match = plans.find((p) => p.stripeId === initialPlanStripeId);
    if (match) {
      setSelectedPlan(match);
    }
  }, [initialPlanStripeId]);

  function handleStoreSelectedPlan() {
    localStorage.setItem("selectedPlan", selectedPlan.stripeId);
  }
  return (
    <section id="pricing" aria-label="Pricing" className="mt-8 pb-32">
      <Container>
        {!inApp && (
          <div className=" flex flex-col items-center">
            <h2 className="gradient-text font-bold text-center text-4xl">
              Simple, transparent pricing
            </h2>
            <p className="text-gray-400 max-w-4xl text-center mt-7 text-balance">
              Start free with 500 credits. Pay only for what you use at 1 credit per API request. 
              No hidden fees, no subscriptions, no surprises. Scale up or down as needed.
            </p>

            <Link
              href="/signup"
              className="text-purple mt-8 underline hover:text-white transition-colors"
              onClick={() =>
                analytics.track("click_create_account", {
                  fromPricing: true,
                  selectedPlan,
                })
              }
            >
              Get started free - no credit card required
            </Link>
          </div>
        )}

        <div className="flex justify-center mt-8">
          <div className="border-[1px] border-[#5E5176] bg-[#C8C8C8]/5 rounded-[20px] w-full max-w-5xl px-8 py-6">
            <p className="text-white text-2xl font-semibold">Choose your plan</p>
            <p className="text-gray-400 text-sm mt-2">1 credit = 1 API request</p>
            <div className="flex mt-8 lg:flex-row flex-col">
              <ul className="flex flex-col lg:w-1/2 sm:pr-8">
                {[...plans.slice(0, 3)].map((plan) => (
                  <PlanOption
                    isSelected={selectedPlan.stripeId === plan.stripeId}
                    key={plan.stripeId}
                    plan={plan}
                    onCheck={() => setSelectedPlan(plan)}
                  />
                ))}
              </ul>
              <ul className="flex flex-col lg:w-1/2 sm:pr-8">
                {[...plans.slice(3, 5)].map((plan) => (
                  <PlanOption
                    isSelected={selectedPlan.stripeId === plan.stripeId}
                    key={plan.stripeId}
                    plan={plan}
                    onCheck={() => setSelectedPlan(plan)}
                  />
                ))}
              </ul>
            </div>
            <div className="w-full h-[1px] bg-[#46414F] mt-8" />
            <div className="flex flex-col mt-8 items-center">
              <p className="text-white text-xl font-semibold">All plans include</p>
              <p className="text-gray-50 text-sm mt-4 sm:mt-2 flex gap-1 sm:gap-4 flex-col items-center sm:flex-row">
                <span>Versioning</span>
                <span>•</span>
                <span>Real-time Analytics</span>
                <span>•</span>
                <span>Support</span>
                <span>•</span>
                <span>API Documentation</span>
              </p>

              {!inApp && (
                <Link
                  href={SIGNUP_REDIRECT_TO_BILLING_MODAL}
                  onClick={handleStoreSelectedPlan}
                  className="bg-purple text-white px-8 py-3 rounded-3xl font-bold hover:bg-transparent border border-purple mt-8 sm:min-w-[325px] text-center transition-all duration-200 hover:scale-105"
                >
                  {`Get ${selectedPlan.credits} credits for $${selectedPlan.price}`}
                </Link>
              )}

              {inApp && (
                <button
                  onClick={() => onBuy(selectedPlan.id)}
                  className="bg-purple text-white px-8 py-3 rounded-3xl font-bold hover:bg-transparent border border-purple mt-8 sm:min-w-[325px] text-center transition-all duration-200 hover:scale-105"
                >{`Buy ${selectedPlan.credits} credits for $${selectedPlan.price}`}</button>
              )}
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
}

function PlanOption({
  isSelected = false,
  plan,
  onCheck,
}: {
  isSelected: boolean;
  plan: typeof plans;
  onCheck: () => void;
}) {
  return (
    <li
      className={cx(
        "relative flex w-full items-center py-2 md:pl-4 pr-6 md:pr-8 rounded-xl justify-between border cursor-pointer",
        plan.popular && "pt-4",
        isSelected && "bg-[#202020] border-[#6F30FF]",
        !isSelected && "border-transparent",
        plan.popular && "ring-1 ring-purple/50"
      )}
      onClick={onCheck}
    >
      {plan.popular && (
        <span className="pointer-events-none absolute -top-2.5 right-4 rounded-full bg-purple px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white shadow-md">
          Most popular
        </span>
      )}
      <div className="flex items-center py-2 px-4 rounded-xl">
        <input
          type="radio"
          id={plan.stripeId}
          name={plan.stripeId}
          value={plan.stripeId}
          checked={isSelected}
          onChange={onCheck}
          className={cx(
            "border-[#6F30FF] text-[#6F30FF] focus:ring-[#6F30FF] bg-transparent"
          )}
        />
        <span className="text-white font-bold text-sm md:text-xl ml-2">
          {`${plan.credits} credits`}
        </span>
      </div>
      <p className="text-white">
        <span className="font-bold md:text-xl">${plan.price}</span>{" "}
        <span className="text-gray-100 text-sm">usd</span>
      </p>
    </li>
  );
}
